/**
 * Brand-native datetime picker.
 *
 * Replaces `<input type="datetime-local">` (and `type="date"` / `type="time"`)
 * because Chrome renders those with a white system popover that clashes
 * with the dark Quotologies theme. This component reuses the same
 * tokens as the rest of the design system: `--ql-yellow` for selection,
 * `--ql-card` for the popover surface, `--ql-fg` / `--ql-fg-muted`
 * for text, JetBrains Mono for numerals.
 *
 * Input/output format: ISO-LIKE local datetime, "YYYY-MM-DDTHH:MM"
 * (no timezone suffix, no seconds) — the same shape browsers emit for
 * `<input type="datetime-local">`, so existing callers don't have to
 * change how they parse the value.
 */
import { useEffect, useRef, useState } from "react";
import { Calendar as CalIcon, ChevronLeft, ChevronRight, X } from "lucide-react";
import { useIsMobile } from "../lib/useIsMobile";

/** Pad a number to 2 digits with leading zero. */
const pad = (n: number): string => String(n).padStart(2, "0");

/** Format a Date as the input shape we agree on (datetime-local style). */
function fmtLocal(d: Date): string {
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

/** Parse the value back to a Date in local time. Returns null on invalid. */
function parseLocal(s: string): Date | null {
  if (!s) return null;
  const m = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})/.exec(s);
  if (!m) return null;
  const [, y, mo, d, h, mi] = m;
  const dt = new Date(Number(y), Number(mo) - 1, Number(d), Number(h), Number(mi), 0, 0);
  return Number.isNaN(dt.getTime()) ? null : dt;
}

/** Human-friendly summary used inside the trigger button. */
function fmtHuman(d: Date | null): string {
  if (!d) return "Pick a date and time";
  return d.toLocaleString(undefined, {
    weekday: "short",
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
const WEEKDAYS = ["M", "T", "W", "T", "F", "S", "S"]; // Monday-first

/** Build the 42 cells (6 rows × 7 cols) shown in the month grid. Each
 *  cell carries the Date and a flag for whether it's in the displayed
 *  month or spill-over from an adjacent month. */
function buildMonthGrid(year: number, month: number): Array<{ date: Date; inMonth: boolean }> {
  const first = new Date(year, month, 1);
  // JS getDay(): 0=Sun..6=Sat. We want Monday-first; shift so Monday=0.
  const offset = (first.getDay() + 6) % 7;
  const start = new Date(year, month, 1 - offset);
  const cells: Array<{ date: Date; inMonth: boolean }> = [];
  for (let i = 0; i < 42; i++) {
    const d = new Date(start.getFullYear(), start.getMonth(), start.getDate() + i);
    cells.push({ date: d, inMonth: d.getMonth() === month });
  }
  return cells;
}

interface Props {
  /** Current value in "YYYY-MM-DDTHH:MM" local format. */
  value: string;
  /** Called with a value in the same shape on every commit. */
  onChange: (next: string) => void;
  disabled?: boolean;
  /** Optional lower bound (no date earlier than this can be picked).
   *  Same format as `value`. */
  min?: string;
}

export function BrandDateTimePicker({ value, onChange, disabled, min }: Props): React.JSX.Element {
  const isMobile = useIsMobile();
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement | null>(null);

  // The selected Date drives the displayed month. Internal "view" lets
  // the operator browse Mar/Apr without changing the actual selection
  // until they tap a day. We snap view to selected on (re-)open.
  const selected = parseLocal(value);
  const minDate = min ? parseLocal(min) : null;
  const [viewYear, setViewYear] = useState<number>((selected ?? new Date()).getFullYear());
  const [viewMonth, setViewMonth] = useState<number>((selected ?? new Date()).getMonth());

  // When opening, jump the calendar to whatever month the current
  // value lives in. Without this, browsing in April and then changing
  // value programmatically (e.g. operator hits "publish now" toggle)
  // would leave the popover frozen on the old month.
  useEffect(() => {
    if (!open) return;
    const d = parseLocal(value) ?? new Date();
    setViewYear(d.getFullYear());
    setViewMonth(d.getMonth());
  }, [open, value]);

  // Close on outside click / Escape.
  useEffect(() => {
    if (!open) return;
    const onDocMouse = (e: MouseEvent): void => {
      if (!rootRef.current?.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", onDocMouse);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDocMouse);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  const stepMonth = (delta: number): void => {
    const m = viewMonth + delta;
    const newYear = viewYear + Math.floor(m / 12);
    const newMonth = ((m % 12) + 12) % 12;
    setViewYear(newYear);
    setViewMonth(newMonth);
  };

  const pickDay = (d: Date): void => {
    // Preserve current hour/minute; replace y/m/d only. Operators
    // expect day-picking to keep their typed-in time.
    const cur = parseLocal(value) ?? new Date();
    const next = new Date(d.getFullYear(), d.getMonth(), d.getDate(), cur.getHours(), cur.getMinutes(), 0, 0);
    if (minDate && next < minDate) {
      // Clamp to min — never let onChange emit a value below the min.
      onChange(fmtLocal(minDate));
      return;
    }
    onChange(fmtLocal(next));
  };

  const setTime = (hh: number, mm: number): void => {
    const cur = parseLocal(value) ?? new Date();
    const next = new Date(cur.getFullYear(), cur.getMonth(), cur.getDate(), hh, mm, 0, 0);
    if (minDate && next < minDate) {
      onChange(fmtLocal(minDate));
      return;
    }
    onChange(fmtLocal(next));
  };

  const cells = buildMonthGrid(viewYear, viewMonth);
  const today = new Date();
  const todayKey = `${today.getFullYear()}-${today.getMonth()}-${today.getDate()}`;
  const selKey = selected
    ? `${selected.getFullYear()}-${selected.getMonth()}-${selected.getDate()}`
    : null;

  return (
    <div ref={rootRef} style={{ position: "relative", width: "100%" }}>
      {/* Trigger — matches the visual style of the other inputs in
          the modal (rgba bg, thin border, JetBrains Mono numerals). */}
      <button
        type="button"
        onClick={() => !disabled && setOpen((o) => !o)}
        disabled={disabled}
        style={{
          width: "100%",
          display: "flex",
          alignItems: "center",
          gap: 10,
          padding: "9px 12px",
          borderRadius: 8,
          border: `1px solid ${open ? "rgba(255,213,0,.40)" : "rgba(255,255,255,.10)"}`,
          background: "rgba(255,255,255,.04)",
          color: "var(--ql-fg)",
          fontSize: 12,
          fontFamily: "var(--ql-font-mono)",
          cursor: disabled ? "not-allowed" : "pointer",
          textAlign: "left",
          outline: "none",
          transition: "border-color 120ms ease",
        }}
      >
        <CalIcon size={13} color="var(--ql-yellow)" strokeWidth={2} />
        <span style={{ flex: 1, color: selected ? "var(--ql-fg)" : "var(--ql-fg-subtle)" }}>
          {fmtHuman(selected)}
        </span>
      </button>

      {open && (
        <div
          style={{
            position: "absolute",
            top: "calc(100% + 6px)",
            left: 0,
            zIndex: 50,
            width: isMobile ? "100%" : 320,
            maxWidth: "min(320px, calc(100vw - 24px))",
            padding: 14,
            borderRadius: 14,
            background: "rgba(16,18,27,.98)",
            border: "1px solid rgba(255,213,0,.18)",
            boxShadow: "0 18px 40px rgba(0,0,0,.55)",
            backdropFilter: "blur(8px)",
          }}
        >
          {/* Header: month nav + close */}
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 6, marginBottom: 10 }}>
            <button
              type="button"
              onClick={() => stepMonth(-1)}
              aria-label="Previous month"
              style={navBtnStyle()}
            >
              <ChevronLeft size={14} strokeWidth={2} />
            </button>
            <div style={{
              flex: 1,
              textAlign: "center",
              fontSize: 12,
              fontWeight: 700,
              color: "var(--ql-yellow)",
              fontFamily: "var(--ql-font-mono)",
              letterSpacing: ".08em",
              textTransform: "uppercase",
            }}>
              {MONTHS[viewMonth]} {viewYear}
            </div>
            <button
              type="button"
              onClick={() => stepMonth(1)}
              aria-label="Next month"
              style={navBtnStyle()}
            >
              <ChevronRight size={14} strokeWidth={2} />
            </button>
            <button
              type="button"
              onClick={() => setOpen(false)}
              aria-label="Close"
              style={{ ...navBtnStyle(), marginLeft: 4 }}
            >
              <X size={13} strokeWidth={2} />
            </button>
          </div>

          {/* Weekday header */}
          <div style={{
            display: "grid",
            gridTemplateColumns: "repeat(7, 1fr)",
            gap: 2,
            marginBottom: 6,
            paddingInline: 2,
          }}>
            {WEEKDAYS.map((w, i) => (
              <div key={i} style={{
                fontSize: 10,
                fontWeight: 700,
                color: "var(--ql-fg-subtle)",
                textAlign: "center",
                fontFamily: "var(--ql-font-mono)",
                letterSpacing: ".10em",
              }}>{w}</div>
            ))}
          </div>

          {/* Day grid */}
          <div style={{ display: "grid", gridTemplateColumns: "repeat(7, 1fr)", gap: 2 }}>
            {cells.map(({ date, inMonth }, idx) => {
              const k = `${date.getFullYear()}-${date.getMonth()}-${date.getDate()}`;
              const isToday = k === todayKey;
              const isSel = k === selKey;
              const disabledCell = !!minDate && date < new Date(minDate.getFullYear(), minDate.getMonth(), minDate.getDate());
              return (
                <button
                  key={idx}
                  type="button"
                  onClick={() => !disabledCell && pickDay(date)}
                  disabled={disabledCell}
                  style={{
                    height: 34,
                    borderRadius: 8,
                    border: isToday && !isSel ? "1px solid rgba(255,213,0,.50)" : "1px solid transparent",
                    background: isSel ? "var(--ql-yellow)" : "transparent",
                    color: isSel
                      ? "#0A0908"
                      : disabledCell
                        ? "var(--ql-fg-faint)"
                        : inMonth
                          ? "var(--ql-fg)"
                          : "var(--ql-fg-subtle)",
                    fontSize: 12,
                    fontWeight: isSel ? 800 : inMonth ? 600 : 400,
                    fontFamily: "var(--ql-font-mono)",
                    cursor: disabledCell ? "not-allowed" : "pointer",
                    opacity: disabledCell ? 0.35 : 1,
                    padding: 0,
                    transition: "background 80ms ease",
                  }}
                  onMouseEnter={(e) => {
                    if (!isSel && !disabledCell) e.currentTarget.style.background = "rgba(255,213,0,.08)";
                  }}
                  onMouseLeave={(e) => {
                    if (!isSel) e.currentTarget.style.background = "transparent";
                  }}
                >
                  {date.getDate()}
                </button>
              );
            })}
          </div>

          {/* Time row */}
          <div style={{
            marginTop: 12,
            paddingTop: 12,
            borderTop: "1px solid rgba(255,255,255,.06)",
            display: "flex",
            alignItems: "center",
            gap: 10,
          }}>
            <span style={{
              fontSize: 10,
              fontWeight: 700,
              letterSpacing: ".10em",
              color: "var(--ql-fg-subtle)",
              fontFamily: "var(--ql-font-mono)",
            }}>TIME</span>
            <TimeBox
              value={selected?.getHours() ?? 0}
              max={23}
              onChange={(hh) => setTime(hh, selected?.getMinutes() ?? 0)}
            />
            <span style={{ color: "var(--ql-fg-muted)", fontWeight: 700, fontFamily: "var(--ql-font-mono)" }}>:</span>
            <TimeBox
              value={selected?.getMinutes() ?? 0}
              max={59}
              step={5}
              onChange={(mm) => setTime(selected?.getHours() ?? 0, mm)}
            />
            <button
              type="button"
              onClick={() => {
                const now = new Date();
                onChange(fmtLocal(now));
              }}
              style={{
                marginLeft: "auto",
                padding: "5px 10px",
                borderRadius: 8,
                border: "1px solid rgba(255,213,0,.30)",
                background: "rgba(255,213,0,.08)",
                color: "var(--ql-yellow)",
                fontSize: 10,
                fontWeight: 700,
                letterSpacing: ".10em",
                textTransform: "uppercase",
                fontFamily: "var(--ql-font-mono)",
                cursor: "pointer",
              }}
            >
              Now
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

/* ---------- internal helpers ---------- */

function navBtnStyle(): React.CSSProperties {
  return {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    width: 28,
    height: 28,
    borderRadius: 7,
    border: "1px solid rgba(255,255,255,.08)",
    background: "rgba(255,255,255,.03)",
    color: "var(--ql-fg)",
    cursor: "pointer",
  };
}

/** A pair of stacked up/down arrows next to a 2-digit number. Wraps
 *  around at min/max so the operator can drag through midnight or
 *  through the day boundary without backtracking. */
function TimeBox({
  value, max, onChange, step = 1,
}: {
  value: number;
  max: number;
  step?: number;
  onChange: (next: number) => void;
}): React.JSX.Element {
  const wrap = (n: number): number => {
    const span = max + 1;
    return ((n % span) + span) % span;
  };
  return (
    <div style={{
      display: "inline-flex", alignItems: "center", gap: 4,
      padding: "4px 6px",
      borderRadius: 8,
      border: "1px solid rgba(255,255,255,.10)",
      background: "rgba(255,255,255,.04)",
    }}>
      <input
        type="text"
        inputMode="numeric"
        value={pad(value)}
        onChange={(e) => {
          const s = e.target.value.replace(/[^\d]/g, "").slice(0, 2);
          if (s === "") return;
          const n = Number(s);
          if (!Number.isNaN(n) && n >= 0 && n <= max) onChange(n);
        }}
        style={{
          width: 26,
          padding: 0,
          border: 0,
          background: "transparent",
          color: "var(--ql-fg)",
          fontSize: 13,
          fontWeight: 700,
          fontFamily: "var(--ql-font-mono)",
          textAlign: "center",
          outline: "none",
        }}
      />
      <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
        <button
          type="button"
          onClick={() => onChange(wrap(value + step))}
          aria-label="Increase"
          style={tinyArrowBtn()}
        >▴</button>
        <button
          type="button"
          onClick={() => onChange(wrap(value - step))}
          aria-label="Decrease"
          style={tinyArrowBtn()}
        >▾</button>
      </div>
    </div>
  );
}

function tinyArrowBtn(): React.CSSProperties {
  return {
    width: 16, height: 12, padding: 0,
    display: "flex", alignItems: "center", justifyContent: "center",
    border: 0,
    background: "transparent",
    color: "var(--ql-fg-muted)",
    fontSize: 9,
    cursor: "pointer",
    lineHeight: 1,
  };
}
